import { Optional } from "../../../utils/Optional";
import { Vocabulary } from "../../Vocabulary.tsx";
import { LatinWord } from "../LatinWord.tsx";
import { searchLatinAdjective, searchLatinPluralAdjective } from "../shared/LatinAdjectives.tsx";
import { searchLatinAdverb } from "../shared/LatinAdverbs.tsx";
import { searchLatinConjunction } from "../shared/LatinConjunction.tsx";
import { searchLatinInterjection } from "../shared/LatinInterjection.tsx";
import { searchLatinNegation } from "../shared/LatinNegations.tsx";
import { searchLatinNoun } from "../shared/LatinNouns.tsx";
import { searchLatinPrepositionAblative, searchLatinPrepositionAccusative } from "../shared/LatinPrepositions.tsx";
import { searchLatinDemonstrativePronoun, searchLatinIndefinitePronoun, searchLatinInterrogativePronoun, searchLatinPersonalPronoun, searchLatinPossessivePronoun } from "../shared/LatinPronouns.tsx";
import { searchLatinToFixIrregularWord } from "../shared/LatinToFixes.tsx";
import { searchLatinConjugatedVerb, searchLatinVerb } from "../shared/LatinVerbs.tsx";

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

interface Latin2025PegasusNovus3Extras {
  _2025_pegasus_novus_3: {
    section: string;
    logicalIndex: number;
    page: number;
  };
}

function _search<A, T>(
  section: string,
  logicalIndex: number,
  page: number,
  f: (root: A) => T,
  root: A,
  extras?: Optional<T>,
): T & Latin2025PegasusNovus3Extras {
  return {
    ...f(root),
    ...extras ?? {},
    _2025_pegasus_novus_3: {
        section,
        logicalIndex,
        page,
    },
  };
}

export const LATIN_2025_PEGASUS_NOVUS_3_VOCABULARY: Vocabulary<"la", LatinWord & Latin2025PegasusNovus3Extras> = {
  language: "la",
  vocabulary: [
    _search("1.0",   1,  11, searchLatinNoun, "avus"),
    _search("1.0",   2,  11, searchLatinNoun, "amīcus"),
    _search("1.0",   3,  11, searchLatinNoun, "deus"),
    _search("1.0",   4,  11, searchLatinNoun, "dominus"),
    _search("1.0",   5,  11, searchLatinNoun, "fīlius"),
    _search("1.0",   6,  11, searchLatinNoun, "locus"),
    _search("1.0",   7,  11, searchLatinNoun, "populus"),
    _search("1.0",   8,  11, searchLatinNoun, "servus"),
    _search("1.0",   9,  11, searchLatinNoun, "puer"),
    _search("1.0",  10,  11, searchLatinNoun, "rosa"),
    _search("1.0",  11,  11, searchLatinNoun, "amīcitia"),
    _search("1.0",  12,  11, searchLatinNoun, "dea"),
    _search("1.0",  13,  11, searchLatinNoun, "fīlia"),
    _search("1.0",  14,  11, searchLatinNoun, "glōria"),
    _search("1.0",  15,  11, searchLatinNoun, "grātia"),
    _search("1.0",  16,  11, searchLatinNoun, "iniūria"),
    _search("1.0",  17,  11, searchLatinNoun, "īnsula"),
    _search("1.0",  18,  11, searchLatinNoun, "puella"),
    _search("1.0",  19,  11, searchLatinNoun, "pugna"),
    _search("1.0",  20,  11, searchLatinNoun, "terra"),
    _search("1.0",  21,  11, searchLatinNoun, "victōria"),
    _search("1.0",  22,  11, searchLatinNoun, "vīta"),
    _search("1.0",  23,  11, searchLatinNoun, "dīvitiae"),
    _search("1.0",  24,  11, searchLatinNoun, "dōnum"),
    _search("1.0",  25,  11, searchLatinNoun, "aedificium"),
    _search("1.0",  26,  11, searchLatinNoun, "forum"),
    _search("1.0",  27,  11, searchLatinNoun, "gaudium"),
    _search("1.0",  28,  11, searchLatinNoun, "imperium"),
    _search("1.0",  29,  11, searchLatinNoun, "incendium"),
    _search("1.0",  30,  11, searchLatinNoun, "templum"),
    _search("1.0",  31,  11, searchLatinNoun, "vīnum"),
    _search("1.0",  32,  11, searchLatinNoun, "castra"),
    _search("1.0",  33,  11, searchLatinAdjective, "bon"),
    _search("1.0",  34,  11, searchLatinAdjective, "aequ"),
    _search("1.0",  35,  11, searchLatinAdjective, "aure"),
    _search("1.0",  36,  11, searchLatinAdjective, "Graec"),
    _search("1.0",  37,  11, searchLatinAdjective, "inimīc"),
    _search("1.0",  38,  11, searchLatinAdjective, "inīqu"),
    _search("1.0",  39,  11, searchLatinAdjective, "īrāt"),
    _search("1.0",  40,  11, searchLatinAdjective, "long"),
    _search("1.0",  41,  11, searchLatinAdjective, "magn"),
    _search("1.0",  42,  12, searchLatinAdjective, "mal"),
    _search("1.0",  43,  12, searchLatinAdjective, "medi"),
    _search("1.0",  44,  12, searchLatinAdjective, "nov"),
    _search("1.0",  45,  12, searchLatinAdjective, "nūll"),
    _search("1.0",  46,  12, searchLatinAdjective, "patri"),
    _search("1.0",  47,  12, searchLatinAdjective, "Rōmān"),
    _search("1.0",  48,  12, searchLatinAdjective, "sōl"),
    _search("1.0",  49,  12, searchLatinAdjective, "timid"),
    _search("1.0",  50,  12, searchLatinAdjective, "tōt"),
    _search("1.0",  51,  12, searchLatinAdjective, "pulchr"),
    _search("1.0",  52,  12, searchLatinNoun, "dux"),
    _search("1.0",  53,  12, searchLatinNoun, "amor"),
    _search("1.0",  54,  12, searchLatinNoun, "cīvis"),
    _search("1.0",  55,  12, searchLatinNoun, "clāmor"),
    _search("1.0",  56,  12, searchLatinNoun, "dolor"),
    _search("1.0",  57,  12, searchLatinNoun, "error"),
    _search("1.0",  58,  12, searchLatinNoun, "frāter"),
    _search("1.0",  59,  12, searchLatinNoun, "homō"),
    _search("1.0",  60,  12, searchLatinNoun, "hostis"),
    _search("1.0",  61,  12, searchLatinNoun, "imperātor"),
    _search("1.0",  62,  12, searchLatinNoun, "labor"),
    _search("1.0",  63,  12, searchLatinNoun, "mīles"),
    _search("1.0",  64,  12, searchLatinNoun, "mōns"),
    _search("1.0",  65,  12, searchLatinNoun, "pater"),
    _search("1.0",  66,  12, searchLatinNoun, "terror"),
    _search("1.0",  67,  12, searchLatinNoun, "timor"),
    _search("1.0",  68,  12, searchLatinNoun, "victor"),
    _search("1.0",  69,  12, searchLatinNoun, "māter"),
    _search("1.0",  70,  12, searchLatinNoun, "celeritās"),
    _search("1.0",  71,  12, searchLatinNoun, "cīvitās"),
    _search("1.0",  72,  12, searchLatinNoun, "damnātiō"),
    _search("1.0",  73,  12, searchLatinNoun, "facultās"),
    _search("1.0",  74,  12, searchLatinNoun, "lībertās"),
    _search("1.0",  75,  12, searchLatinNoun, "mors"),
    _search("1.0",  76,  12, searchLatinNoun, "mulier"),
    _search("1.0",  77,  12, searchLatinNoun, "multitūdō"),
    _search("1.0",  78,  12, searchLatinNoun, "nox"),
    _search("1.0",  79,  12, searchLatinNoun, "pars"),
    _search("1.0",  80,  12, searchLatinNoun, "soror"),
    _search("1.0",  81,  12, searchLatinNoun, "urbs"),
    _search("1.0",  82,  12, searchLatinNoun, "uxor"),
    _search("1.0",  83,  12, searchLatinNoun, "corpus"),
    _search("1.0",  84,  12, searchLatinNoun, "certāmen"),
    _search("1.0",  85,  13, searchLatinNoun, "flūmen"),
    _search("1.0",  86,  13, searchLatinNoun, "lītus"),
    _search("1.0",  87,  13, searchLatinNoun, "nōmen"),
    _search("1.0",  88,  13, searchLatinNoun, "tempus"),
    _search("1.0",  89,  13, searchLatinAdjective, "fort"),
    _search("1.0",  90,  13, searchLatinAdjective, "atrōc"),
    _search("1.0",  91,  13, searchLatinAdjective, "audāc"),
    _search("1.0",  92,  13, searchLatinAdjective, "ingent"),
    _search("1.0",  93,  13, searchLatinAdjective, "sapient"),
    _search("1.0",  94,  13, searchLatinAdjective, "celer"),
    _search("1.0",  95,  13, searchLatinAdjective, "facil"),
    _search("1.0",  96,  13, searchLatinAdjective, "grav"),
    _search("1.0",  97,  13, searchLatinAdjective, "mortāl"),
    _search("1.0",  98,  13, searchLatinAdjective, "omn"),
    _search("1.0",  99,  13, searchLatinPluralAdjective, "omni"),
    _search("1.0", 100,  13, searchLatinAdjective, "terribil"),
    _search("1.0", 101,  13, searchLatinAdjective, "trīst"),
    _search("1.0", 102,  13, searchLatinAdjective, "ūtil"),
    _search("1.0", 103,  13, searchLatinNoun, "fructus"),
    _search("1.0", 104,  13, searchLatinNoun, "aditus"),
    _search("1.0", 105,  13, searchLatinNoun, "cāsus"),
    _search("1.0", 106,  13, searchLatinNoun, "cōnspectus"),
    _search("1.0", 107,  13, searchLatinNoun, "cursus"),
    _search("1.0", 108,  13, searchLatinNoun, "exitus"),
    _search("1.0", 109,  13, searchLatinNoun, "mōtus"),
    _search("1.0", 110,  13, searchLatinNoun, "ūsus"),
    _search("1.0", 111,  13, searchLatinNoun, "domus"),
    _search("1.0", 112,  13, searchLatinNoun, "manus"),
    _search("1.0", 113,  13, searchLatinNoun, "rēs"),
    _search("1.0", 114,  13, searchLatinNoun, "diēs"),
    _search("1.0", 115,  13, searchLatinPersonalPronoun, "ego"),
    _search("1.0", 116,  13, searchLatinPersonalPronoun, "tū"),
    _search("1.0", 117,  13, searchLatinPersonalPronoun, "is, ea, id"),
    _search("1.0", 118,  13, searchLatinPersonalPronoun, "sē"),
    _search("1.0", 119,  13, searchLatinPersonalPronoun, "nōs"),
    _search("1.0", 120,  13, searchLatinPersonalPronoun, "vōs"),
    _search("1.0", 121,  13, searchLatinPossessivePronoun, "meus, ~a, ~um"),
    _search("1.0", 122,  13, searchLatinPossessivePronoun, "tuus, ~a, ~um"),
    _search("1.0", 123,  13, searchLatinPossessivePronoun, "suus, ~a, ~um"),
    _search("1.0", 124,  13, searchLatinPossessivePronoun, "noster, nostra, nostrum"),
    _search("1.0", 125,  13, searchLatinPossessivePronoun, "vester, vestra, vestrum"),
    _search("1.0", 126,  14, searchLatinDemonstrativePronoun, "hic, haec, hoc; huius"),
    _search("1.0", 127,  14, searchLatinDemonstrativePronoun, "īdem, eadem, idem; eiusdem"),
    _search("1.0", 128,  14, searchLatinDemonstrativePronoun, "ille, illa, illud; illīus"),
    _search("1.0", 129,  14, searchLatinDemonstrativePronoun, "ipse, ipsa, ipsum; ipsīus"),
    _search("1.0", 130,  14, searchLatinDemonstrativePronoun, "iste, ista, istud; istīus"),
    _search("1.0", 131,  14, searchLatinIndefinitePronoun, "nēmō"),
    _search("1.0", 132,  14, searchLatinIndefinitePronoun, "nihil"),
    _search("1.0", 133,  14, searchLatinPrepositionAccusative, "ad"),
    _search("1.0", 134,  14, searchLatinPrepositionAccusative, "ante"),
    _search("1.0", 135,  14, searchLatinPrepositionAccusative, "apud"),
    _search("1.0", 136,  14, searchLatinPrepositionAccusative, "contrā"),
    _search("1.0", 137,  14, searchLatinPrepositionAccusative, "in"),
    _search("1.0", 138,  14, searchLatinPrepositionAccusative, "ob"),
    _search("1.0", 139,  14, searchLatinPrepositionAccusative, "per"),
    _search("1.0", 140,  14, searchLatinPrepositionAccusative, "post"),
    _search("1.0", 141,  14, searchLatinPrepositionAccusative, "praeter"),
    _search("1.0", 142,  14, searchLatinPrepositionAccusative, "propter"),
    _search("1.0", 143,  14, searchLatinPrepositionAccusative, "sub"),
    _search("1.0", 144,  14, searchLatinPrepositionAccusative, "super"),
    _search("1.0", 145,  14, searchLatinPrepositionAccusative, "trāns"),
    _search("1.0", 146,  14, searchLatinPrepositionAblative, "ā of ab"),
    _search("1.0", 147,  14, searchLatinPrepositionAblative, "cum"),
    _search("1.0", 148,  14, searchLatinPrepositionAblative, "dē"),
    _search("1.0", 149,  14, searchLatinPrepositionAblative, "ē of ex"),
    _search("1.0", 150,  14, searchLatinPrepositionAblative, "in"),
    _search("1.0", 151,  14, searchLatinPrepositionAblative, "prō"),
    _search("1.0", 152,  14, searchLatinPrepositionAblative, "sine"),
    _search("1.0", 153,  14, searchLatinPrepositionAblative, "sub"),
    _search("1.1", 154,  15, searchLatinNoun, "exemplum"),
    _search("1.1", 155,  15, searchLatinAdjective, "īnfer"),
    _search("1.1", 156,  15, searchLatinAdjective, "invīt"),
    _search("1.1", 157,  15, searchLatinAdjective, "super"),
    _search("1.1", 158,  15, searchLatinAdjective, "valid"),
    _search("1.1", 159,  15, searchLatinAdjective, "dīvit"),
    _search("1.1", 160,  15, searchLatinAdjective, "pauper"),
    _search("1.1", 161,  15, searchLatinAdjective, "potent"),
    _search("1.1", 162,  15, searchLatinInterrogativePronoun, "quis?, quid?; quī, quae, quod?"),
    _search("1.1", 163,  15, searchLatinVerb, "fīerī"),
    _search("1.1", 164,  15, searchLatinAdverb, "cottīdiē"),
    _search("1.1", 165,  15, searchLatinAdverb, "cūr?"),
    _search("1.1", 166,  15, searchLatinAdverb, "nōndum"),
    _search("1.1", 167,  15, searchLatinAdverb, "rūrsus"),
    _search("1.1", 168,  15, searchLatinConjunction, "dum", { dutch: "terwijl; zolang als; totdat", }),
    _search("1.2", 169,  16, searchLatinNoun, "epistula"),
    _search("1.2", 170,  16, searchLatinNoun, "mēnsa"),
    _search("1.2", 171,  16, searchLatinNoun, "fātum"),
    _search("1.2", 172,  16, searchLatinNoun, "vitium"),
    _search("1.2", 173,  16, searchLatinAdjective, "ali"),
    _search("1.2", 174,  16, searchLatinAdjective, "alter"),
    _search("1.2", 175,  16, searchLatinToFixIrregularWord, "alter ..., alter ..."),
    _search("1.2", 176,  16, searchLatinAdjective, "honest"),
    _search("1.2", 177,  16, searchLatinAdjective, "tūt"),
    _search("1.2", 178,  16, searchLatinNoun, "honor"),
    _search("1.2", 179,  16, searchLatinNoun, "virtūs"),
    _search("1.2", 180,  16, searchLatinNoun, "onus"),
    _search("1.2", 181,  16, searchLatinAdjective, "priōr"),
    _search("1.2", 182,  16, searchLatinNoun, "fidēs"),
    _search("1.2", 183,  16, searchLatinIndefinitePronoun, "aliquis, aliqua, aliquid"),
    _search("1.2", 184,  16, searchLatinIndefinitePronoun, "quīdam, quaedam, quiddam"),
    _search("1.2", 185,  16, searchLatinVerb, "cōgitāre"),
    _search("1.2", 186,  16, searchLatinVerb, "iactāre"),
    _search("1.2", 187,  16, searchLatinVerb, "iūdicāre"),
    _search("1.2", 188,  16, searchLatinVerb, "iuvāre"),
    _search("1.2", 189,  16, searchLatinVerb, "pertinēre"),
    _search("1.2", 190,  16, searchLatinVerb, "premere"),
    _search("1.2", 191,  16, searchLatinAdverb, "parum"),
    _search("1.2", 192,  16, searchLatinAdverb, "satis"),
    _search("1.2", 193,  16, searchLatinConjunction, "cum + ind."),
    _search("1.3", 194,  16, searchLatinNoun, "lūdus"),
    _search("1.3", 195,  16, searchLatinNoun, "fortūna"),
    _search("1.3", 196,  16, searchLatinAdjective, "grāt"),
    _search("1.3", 197,  16, searchLatinAdjective, "maest"),
    _search("1.3", 198,  16, searchLatinAdjective, "saev"),
    _search("1.3", 199,  16, searchLatinAdjective, "vīv"),
    _search("1.3", 200,  16, searchLatinNoun, "potestās"),
    _search("1.3", 201,  16, searchLatinVerb, "lūdere"),
    _search("1.3", 202,  16, searchLatinInterjection, "ecce!"),
    _search("1.4", 203,  17, searchLatinNoun, "vir"),
    _search("1.4", 204,  17, searchLatinNoun, "cōpia"),
    _search("1.4", 205,  17, searchLatinNoun, "cōpiae"),
    _search("1.4", 206,  17, searchLatinNoun, "frūmentum"),
    _search("1.4", 207,  17, searchLatinAdjective, "magnific"),
    _search("1.4", 208,  17, searchLatinNoun, "vīs", { dutch: "de kracht; het geweld (geen gen.)", }),
    _search("1.4", 209,  17, searchLatinAdjective, "pār"),
    _search("1.4", 210,  17, searchLatinAdjective, "veter"),
    _search("1.4", 211,  17, searchLatinVerb, "appropinquāre"),
    _search("1.4", 212,  17, searchLatinVerb, "praebēre"),
    _search("1.4", 213,  17, searchLatinVerb, "cōnfīdere"),
    _search("1.4", 214,  17, searchLatinVerb, "contendere"),
    _search("1.4", 215,  17, searchLatinVerb, "crēdere"),
    _search("1.4", 216,  17, searchLatinVerb, "dēligere"),
    _search("1.4", 217,  17, searchLatinVerb, "fundere"),
    _search("1.4", 218,  17, searchLatinVerb, "reperīre"),
    _search("1.5", 219,  17, searchLatinAdjective, "cupid"),
    _search("1.5", 220,  17, searchLatinAdjective, "ignār"),
    _search("1.5", 221,  17, searchLatinAdjective, "plēn"),
    _search("1.5", 222,  17, searchLatinVerb, "līberāre"),
    _search("1.5", 223,  17, searchLatinVerb, "occurrere"),
    _search("1.5", 224,  17, searchLatinAdverb, "omnīnō"),
    _search("1.6", 225,  18, searchLatinNoun, "sententia"),
    _search("1.6", 226,  18, searchLatinNoun, "iūdicium"),
    _search("1.6", 227,  18, searchLatinAdjective, "callid"),
    _search("1.6", 228,  18, searchLatinAdjective, "quiēt"),
    _search("1.6", 229,  18, searchLatinNoun, "iūdex"),
    _search("1.6", 230,  18, searchLatinNoun, "māiōrēs"),
    _search("1.6", 231,  18, searchLatinNoun, "avis"),
    _search("1.6", 232,  18, searchLatinAdjective, "bon"),
    _search("1.6", 233,  18, searchLatinAdjective, "meli"),
    _search("1.6", 234,  18, searchLatinAdjective, "optim"),
    _search("1.6", 235,  18, searchLatinAdjective, "mal"),
    _search("1.6", 236,  18, searchLatinAdjective, "pēi"),
    _search("1.6", 237,  18, searchLatinAdjective, "pessim"),
    _search("1.6", 238,  18, searchLatinAdjective, "magn"),
    _search("1.6", 239,  18, searchLatinAdjective, "māi"),
    _search("1.6", 240,  18, searchLatinAdjective, "maxim"),
    _search("1.6", 241,  18, searchLatinAdjective, "parv"),
    _search("1.6", 242,  18, searchLatinAdjective, "min"),
    _search("1.6", 243,  18, searchLatinAdjective, "minim"),
    _search("1.6", 244,  18, searchLatinAdjective, "mult"),
    _search("1.6", 245,  18, searchLatinAdjective, "plūr"),
    _search("1.6", 246,  18, searchLatinAdverb, "plūs"),
    _search("1.6", 247,  18, searchLatinAdjective, "plēr"),
    _search("1.6", 248,  18, searchLatinAdverb, "plērumque"),
    _search("1.6", 249,  18, searchLatinAdjective, "plūrim"),
    _search("1.6", 250,  18, searchLatinAdverb, "plūrimum"),
    _search("1.6", 251,  18, searchLatinAdverb, "longē"),
    _search("1.6", 252,  18, searchLatinAdverb, "multum (multō)"),
    _search("1.6", 253,  18, searchLatinAdverb, "quam"),
    _search("1.7", 254,  18, searchLatinNoun, "asinus"),
    _search("1.7", 255,  18, searchLatinAdjective, "dīvers"),
    _search("1.7", 256,  18, searchLatinAdjective, "parāt"),
    _search("1.7", 257,  18, searchLatinNoun, "arbor"),
    _search("1.7", 258,  18, searchLatinVerb, "maerēre"),
    _search("1.7", 259,  18, searchLatinVerb, "reddere"),
    _search("2.0", 260,  23, searchLatinVerb, "amāre"),
    _search("2.0", 261,  23, searchLatinVerb, "certāre"),
    _search("2.0", 262,  23, searchLatinVerb, "clāmāre"),
    _search("2.0", 263,  23, searchLatinVerb, "cūrāre", { dutch: "verzorgen; zorgen voor", }),
    _search("2.0", 264,  23, searchLatinVerb, "dare"),
    _search("2.0", 265,  23, searchLatinVerb, "exspectāre"),
    _search("2.0", 266,  23, searchLatinVerb, "intrāre"),
    _search("2.0", 267,  23, searchLatinVerb, "nāvigāre"),
    _search("2.0", 268,  23, searchLatinVerb, "nūntiāre"),
    _search("2.0", 269,  23, searchLatinVerb, "pugnāre"),
    _search("2.0", 270,  23, searchLatinVerb, "rogāre"),
    _search("2.0", 271,  23, searchLatinVerb, "spērāre"),
    _search("2.0", 272,  23, searchLatinVerb, "stāre"),
    _search("2.0", 273,  23, searchLatinVerb, "monēre"),
    _search("2.0", 274,  23, searchLatinVerb, "habēre"),
    _search("2.0", 275,  23, searchLatinVerb, "timēre"),
    _search("2.0", 276,  23, searchLatinVerb, "vidēre"),
    _search("2.0", 277,  23, searchLatinVerb, "prōvidēre"),
    _search("2.0", 278,  23, searchLatinVerb, "tegere"),
    _search("2.0", 279,  23, searchLatinVerb, "cēdere"),
    _search("2.0", 280,  23, searchLatinVerb, "accēdere"),
    _search("2.0", 281,  23, searchLatinVerb, "discēdere"),
    _search("2.0", 282,  23, searchLatinVerb, "prōcēdere"),
    _search("2.0", 283,  23, searchLatinVerb, "dūcere"),
    _search("2.0", 284,  23, searchLatinVerb, "addūcere"),
    _search("2.0", 285,  23, searchLatinVerb, "dēdūcere"),
    _search("2.0", 286,  23, searchLatinVerb, "pōnere"),
    _search("2.0", 287,  23, searchLatinVerb, "compōnere"),
    _search("2.0", 288,  23, searchLatinVerb, "dēpōnere"),
    _search("2.0", 289,  23, searchLatinVerb, "expōnere"),
    _search("2.0", 290,  23, searchLatinVerb, "impōnere"),
    _search("2.0", 291,  23, searchLatinVerb, "dīcere", { dutch: "zeggen; spreken", }),
    _search("2.0", 292,  23, searchLatinVerb, "mittere"),
    _search("2.0", 293,  23, searchLatinVerb, "dīmittere"),
    _search("2.0", 294,  23, searchLatinVerb, "expellere"),
    _search("2.0", 295,  23, searchLatinVerb, "extrahere"),
    _search("2.0", 296,  23, searchLatinVerb, "incidere"),
    _search("2.0", 297,  23, searchLatinVerb, "metuere"),
    _search("2.0", 298,  23, searchLatinVerb, "repetere"),
    _search("2.0", 299,  24, searchLatinVerb, "audīre"),
    _search("2.0", 300,  24, searchLatinVerb, "dormīre"),
    _search("2.0", 301,  24, searchLatinVerb, "fīnīre"),
    _search("2.0", 302,  24, searchLatinVerb, "venīre"),
    _search("2.0", 303,  24, searchLatinVerb, "convenīre"),
    _search("2.0", 304,  24, searchLatinVerb, "pervenīre", { dutch: "bereiken", }),
    _search("2.0", 305,  24, searchLatinVerb, "capere", { dutch: "(in)nemen; krijgen", }),
    _search("2.0", 306,  24, searchLatinVerb, "accipere"),
    _search("2.0", 307,  24, searchLatinVerb, "excipere"),
    _search("2.0", 308,  24, searchLatinVerb, "cōnspicere"),
    _search("2.0", 309,  24, searchLatinVerb, "respicere"),
    _search("2.0", 310,  24, searchLatinVerb, "rapere"),
    _search("2.0", 311,  24, searchLatinVerb, "corripere"),
    _search("2.0", 312,  24, searchLatinVerb, "ēripere"),
    _search("2.0", 313,  24, searchLatinVerb, "iacere"),
    _search("2.0", 314,  24, searchLatinVerb, "dēicere"),
    _search("2.0", 315,  24, searchLatinVerb, "facere"),
    _search("2.0", 316,  24, searchLatinVerb, "perficere"),
    _search("2.0", 317,  24, searchLatinToFixIrregularWord, "dīgredī, ~ior"),
    _search("2.0", 318,  24, searchLatinToFixIrregularWord, "ēgredī, ~ior"),
    _search("2.0", 319,  24, searchLatinToFixIrregularWord, "ingredī, ~ior"),
    _search("2.0", 320,  24, searchLatinToFixIrregularWord, "prōgredī, ~ior"),
    _search("2.0", 321,  24, searchLatinVerb, "esse"),
    _search("2.0", 322,  24, searchLatinVerb, "abesse"),
    _search("2.0", 323,  24, searchLatinVerb, "adesse", { dutch: "aanwezig zijn; helpen (+ dat.)", }),
    _search("2.0", 324,  24, searchLatinVerb, "prōdesse", { dutch: "nuttig zijn", }),
    _search("2.0", 325,  24, searchLatinVerb, "superesse"),
    _search("2.0", 326,  24, searchLatinVerb, "posse"),
    _search("2.0", 327,  24, searchLatinVerb, "ferre"),
    _search("2.0", 328,  24, searchLatinVerb, "afferre", { dutch: "brengen (naar)", }),
    _search("2.0", 329,  24, searchLatinVerb, "referre", { dutch: "terugbrengen", }),
    _search("2.0", 330,  24, searchLatinVerb, "trānsferre"),
    _search("2.0", 331,  24, searchLatinConjugatedVerb, "inquit"),
    _search("2.0", 332,  24, searchLatinVerb, "īre"),
    _search("2.0", 333,  24, searchLatinVerb, "abīre"),
    _search("2.0", 334,  24, searchLatinVerb, "adīre"),
    _search("2.0", 335,  24, searchLatinVerb, "exīre"),
    _search("2.0", 336,  24, searchLatinVerb, "inīre"),
    _search("2.0", 337,  24, searchLatinVerb, "redīre"),
    _search("2.0", 338,  24, searchLatinVerb, "trānsīre"),
    _search("2.0", 339,  25, searchLatinVerb, "mālle"),
    _search("2.0", 340,  25, searchLatinVerb, "nōlle"),
    _search("2.0", 341,  25, searchLatinVerb, "velle"),
    _search("2.0", 342,  25, searchLatinAdverb, "deinde"),
    _search("2.0", 343,  25, searchLatinAdverb, "dēnique"),
    _search("2.0", 344,  25, searchLatinAdverb, "domī"),
    _search("2.0", 345,  25, searchLatinAdverb, "domum"),
    _search("2.0", 346,  25, searchLatinAdverb, "etiam", { dutch: "ook; zelfs; nog", }),
    _search("2.0", 347,  25, searchLatinAdverb, "hīc"),
    _search("2.0", 348,  25, searchLatinAdverb, "iam"),
    _search("2.0", 349,  25, searchLatinAdverb, "ibi"),
    _search("2.0", 350,  25, searchLatinAdverb, "ideō"),
    _search("2.0", 351,  25, searchLatinAdverb, "itaque"),
    _search("2.0", 352,  25, searchLatinAdverb, "maximē", { dutch: "het meest; zeer (veel)", }),
    _search("2.0", 353,  25, searchLatinAdverb, "saepe"),
    _search("2.0", 354,  25, searchLatinAdverb, "semper"),
    _search("2.0", 355,  25, searchLatinAdverb, "sīc"),
    _search("2.0", 356,  25, searchLatinAdverb, "simul"),
    _search("2.0", 357,  25, searchLatinAdverb, "statim"),
    _search("2.0", 358,  25, searchLatinAdverb, "subitō"),
    _search("2.0", 359,  25, searchLatinAdverb, "tamen"),
    _search("2.0", 360,  25, searchLatinAdverb, "tandem"),
    _search("2.0", 361,  25, searchLatinAdverb, "tum of tunc", { dutch: "op dat moment; dan", }),
    _search("2.0", 362,  25, searchLatinAdverb, "ubi?"),
    _search("2.0", 363,  25, searchLatinNegation, "nōn"),
    _search("2.0", 364,  25, searchLatinConjunction, "ac (of atque)"),
    _search("2.0", 365,  25, searchLatinConjunction, "at"),
    _search("2.0", 366,  25, searchLatinConjunction, "aut"),
    _search("2.0", 367,  25, searchLatinConjunction, "autem"),
    _search("2.0", 368,  25, searchLatinConjunction, "enim"),
    _search("2.0", 369,  25, searchLatinConjunction, "et"),
    _search("2.0", 370,  25, searchLatinConjunction, "nam"),
    _search("2.0", 371,  25, searchLatinConjunction, "nec of neque"),
    _search("2.0", 372,  25, searchLatinConjunction, "-que"),
    _search("2.0", 373,  25, searchLatinConjunction, "sed"),
    _search("2.0", 374,  25, searchLatinConjunction, "quamquam"),
    _search("2.0", 375,  25, searchLatinConjunction, "quia"),
    _search("2.0", 376,  25, searchLatinConjunction, "quod"),
    _search("2.0", 377,  25, searchLatinConjunction, "sī"),
    _search("2.0", 378,  25, searchLatinConjunction, "ubi"),
    _search("2.1", 379,  25, searchLatinNoun, "ventus"),
    _search("2.1", 380,  26, searchLatinNoun, "vesper"),
    _search("2.1", 381,  26, searchLatinNoun, "nuptiae"),
    _search("2.1", 382,  26, searchLatinNoun, "bracchium"),
    _search("2.1", 383,  26, searchLatinNoun, "impedīmentum"),
    _search("2.1", 384,  26, searchLatinNoun, "sacerdōs", { dutch: "de priester(es)", }),
    _search("2.1", 385,  26, searchLatinNoun, "tempestās"),
    _search("2.1", 386,  26, searchLatinNoun, "turris"),
    _search("2.1", 387,  26, searchLatinNoun, "virgō"),
    _search("2.1", 388,  26, searchLatinAdjective, "cār"),
    _search("2.1", 389,  26, searchLatinAdjective, "crūdēl"),
    _search("2.1", 390,  26, searchLatinToFixIrregularWord, "precārī, ~or", { dutch: "smeken", }),
    _search("2.1", 391,  26, searchLatinToFixIrregularWord, "vidērī, ~eor + dat."),
    _search("2.1", 392,  26, searchLatinVerb, "committere"),
    _search("2.1", 393,  26, searchLatinVerb, "dīvidere"),
    _search("2.1", 394,  26, searchLatinVerb, "iungere"),
    _search("2.1", 395,  26, searchLatinVerb, "parcere"),
    _search("2.1", 396,  26, searchLatinVerb, "tendere"),
    _search("2.1", 397,  26, searchLatinVerb, "trahere"),
    _search("2.1", 398,  26, searchLatinVerb, "conicere"),
    _search("2.1", 399,  26, searchLatinVerb, "effugere"),
    _search("2.1", 400,  26, searchLatinVerb, "inicere"),
    _search("2.1", 401,  26, searchLatinAdverb, "quid?"),
    _search("2.1", 402,  26, searchLatinAdverb, "vērum of vērō"),
    _search("2.1", 403,  26, searchLatinConjunction, "postquam"),
    _search("2.2", 404,  26, searchLatinNoun, "mātrimōnium"),
    _search("2.2", 405,  26, searchLatinAdjective, "dign"),
    _search("2.2", 406,  26, searchLatinNoun, "plēbs"),
    _search("2.2", 407,  26, searchLatinNoun, "sēdēs"),
    _search("2.2", 408,  26, searchLatinNoun, "facinus"),
    _search("2.2", 409,  26, searchLatinNoun, "nūmen"),
    _search("2.2", 410,  26, searchLatinNoun, "speciēs"),
    _search("2.2", 411,  26, searchLatinVerb, "cōnsulere"),
    _search("2.2", 412,  26, searchLatinVerb, "differre"),
  ]
};